import { BackingSkillsCombo } from '@/components/admin/agents/BackingSkillsCombo';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import type {
  AgentApiKeyRow,
  AgentStarterPrompt,
  AgentSummary,
  UpdateAdminAgentRequest,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

export function AdminAgentDetail() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: agent, isLoading } = useQuery({
    queryKey: ['admin', 'agents', id],
    queryFn: () => api.adminAgentGet(id),
  });

  if (isLoading || !agent) {
    return (
      <PageShell>
        <div className="p-6 text-sm text-muted-foreground">불러오는 중...</div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title={agent.name}
        description={`slug: ${agent.slug}`}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/admin/agents' })}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            목록
          </Button>
        }
      />
      <div className="grid gap-6 p-4 lg:grid-cols-2">
        <AgentEditForm agent={agent} qc={qc} />
        <AgentKeysPanel agentId={agent.id} />
      </div>
      <DangerZone agent={agent} onDeleted={() => navigate({ to: '/admin/agents' })} />
    </PageShell>
  );
}

interface EditFormProps {
  agent: AgentSummary;
  qc: ReturnType<typeof useQueryClient>;
}

function AgentEditForm({ agent, qc }: EditFormProps) {
  const [name, setName] = useState(agent.name);
  const [description, setDescription] = useState(agent.description ?? '');
  const [skillIds, setSkillIds] = useState<string[]>(agent.skillIds);
  const [isEnabled, setIsEnabled] = useState(agent.isEnabled);
  const [toolVisibility, setToolVisibility] = useState(agent.toolVisibility);
  const [rateLimit, setRateLimit] = useState(agent.rateLimitPerMin);
  const [prompts, setPrompts] = useState<AgentStarterPrompt[]>(agent.starterPrompts);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setName(agent.name);
    setDescription(agent.description ?? '');
    setSkillIds(agent.skillIds);
    setIsEnabled(agent.isEnabled);
    setToolVisibility(agent.toolVisibility);
    setRateLimit(agent.rateLimitPerMin);
    setPrompts(agent.starterPrompts);
  }, [agent]);

  const mutation = useMutation({
    mutationFn: (body: UpdateAdminAgentRequest) => api.adminAgentUpdate(agent.id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'agents'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'agents', agent.id] });
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '수정 실패');
    },
  });

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (skillIds.length === 0) {
      setError('backing skill 을 최소 1 개 이상 선택하세요.');
      return;
    }
    const cleaned = prompts
      .filter((p) => (p.label ?? '').trim() && (p.prompt ?? '').trim())
      .map((p) => ({ label: p.label?.trim(), prompt: p.prompt?.trim() }));
    mutation.mutate({
      name: name.trim(),
      description: description.trim() || null,
      skillIds,
      isEnabled,
      starterPrompts: cleaned,
      toolVisibility,
      rateLimitPerMin: rateLimit,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-lg border bg-card p-4">
      <div className="text-sm font-bold">기본 정보</div>

      <Field label="이름" htmlFor="agent-name">
        <Input id="agent-name" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      <Field label="설명" htmlFor="agent-desc">
        <textarea
          id="agent-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
        />
      </Field>

      <Field
        label="backing skill (1~4 개)"
        htmlFor="agent-skills"
        hint="2 개 이상 선택하면 system prompt 와 도구가 cross-domain 모드로 머지됩니다."
      >
        <BackingSkillsCombo value={skillIds} onChange={setSkillIds} max={4} />
      </Field>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={(e) => setIsEnabled(e.target.checked)}
          />
          활성화
        </label>
        <label className="flex items-center gap-2 text-sm">
          도구 진행 표시
          <select
            value={toolVisibility}
            onChange={(e) => setToolVisibility(e.target.value as 'hidden' | 'status_only')}
            className="h-8 rounded border border-input bg-transparent px-2 text-xs"
          >
            <option value="status_only">phase status (기본)</option>
            <option value="hidden">완전 숨김</option>
          </select>
        </label>
      </div>

      <Field label="분당 호출 제한 (externalUserRef 별)" htmlFor="agent-rate">
        <Input
          id="agent-rate"
          type="number"
          min={0}
          max={10000}
          value={rateLimit}
          onChange={(e) => setRateLimit(Number(e.target.value) || 0)}
        />
      </Field>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-bold">추천 질문</div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPrompts([...prompts, { label: '', prompt: '' }])}
          >
            <Plus className="mr-1 h-3 w-3" />
            추가
          </Button>
        </div>
        <div className="space-y-2">
          {prompts.length === 0 && <div className="text-xs text-muted-foreground">없음</div>}
          {prompts.map((p, i) => (
            <div key={i} className="flex gap-2">
              <Input
                value={p.label}
                onChange={(e) =>
                  setPrompts(
                    prompts.map((row, idx) =>
                      idx === i ? { label: e.target.value, prompt: row.prompt } : row,
                    ),
                  )
                }
                placeholder="🎯 라벨"
                className="w-1/3"
              />
              <Input
                value={p.prompt}
                onChange={(e) =>
                  setPrompts(
                    prompts.map((row, idx) =>
                      idx === i ? { label: row.label, prompt: e.target.value } : row,
                    ),
                  )
                }
                placeholder="실제 질문 (LLM 에 전달)"
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setPrompts(prompts.filter((_, idx) => idx !== i))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {error && <div className="text-sm text-destructive">{error}</div>}
      {saved && <div className="text-sm text-emerald-600">저장됨.</div>}

      <div className="flex justify-end">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? '저장 중...' : '저장'}
        </Button>
      </div>
    </form>
  );
}

function AgentKeysPanel({ agentId }: { agentId: string }) {
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'agents', agentId, 'keys'],
    queryFn: () => api.adminAgentKeyList(agentId),
  });

  const createMutation = useMutation({
    mutationFn: () => api.adminAgentKeyCreate(agentId, { label: label.trim() || 'default' }),
    onSuccess: (res) => {
      setCreatedSecret(res.apiKey);
      setLabel('');
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'agents', agentId, 'keys'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '발급 실패');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => api.adminAgentKeyRevoke(agentId, keyId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'agents', agentId, 'keys'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (keyId: string) => api.adminAgentKeyDelete(agentId, keyId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'agents', agentId, 'keys'] }),
  });

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <div className="text-sm font-bold">API 키</div>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="mb-1 block text-xs text-muted-foreground" htmlFor="key-label">
            라벨
          </label>
          <Input
            id="key-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="wholesale-prod"
          />
        </div>
        <Button
          type="button"
          onClick={() => createMutation.mutate()}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending ? '발급 중...' : '발급'}
        </Button>
      </div>

      {createdSecret && (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-xs">
          <div className="mb-1 font-bold text-emerald-900">평문 키 (한 번만 표시)</div>
          <div className="break-all font-mono text-emerald-900">{createdSecret}</div>
          <div className="mt-1 text-emerald-800">외부 서버 .env 에 즉시 보관하세요.</div>
        </div>
      )}

      {error && <div className="text-sm text-destructive">{error}</div>}

      <div>
        {isLoading && <div className="text-xs text-muted-foreground">불러오는 중...</div>}
        {data && data.keys.length === 0 && (
          <div className="text-xs text-muted-foreground">발급된 키 없음.</div>
        )}
        <ul className="divide-y">
          {data?.keys.map((k) => (
            <KeyRow
              key={k.id}
              k={k}
              onRevoke={revokeMutation.mutate}
              onDelete={deleteMutation.mutate}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

function KeyRow({
  k,
  onRevoke,
  onDelete,
}: {
  k: AgentApiKeyRow;
  onRevoke: (keyId: string) => void;
  onDelete: (keyId: string) => void;
}) {
  const isRevoked = !!k.revokedAt;
  return (
    <li className="flex items-center gap-2 py-2 text-sm">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className={isRevoked ? 'text-muted-foreground line-through' : 'font-medium'}>
            {k.label}
          </div>
          {isRevoked && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
              폐기됨
            </span>
          )}
        </div>
        <div className="font-mono text-[11px] text-muted-foreground">
          {k.keyPrefix}...
          {k.lastUsedAt ? ` · 마지막 사용 ${new Date(k.lastUsedAt).toLocaleString('ko-KR')}` : ''}
        </div>
      </div>
      {!isRevoked && (
        <Button type="button" variant="ghost" size="sm" onClick={() => onRevoke(k.id)}>
          폐기
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => {
          if (confirm('영구 삭제할까요? 폐기 처리는 위 "폐기" 버튼이 더 안전합니다.')) {
            onDelete(k.id);
          }
        }}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  );
}

function DangerZone({ agent, onDeleted }: { agent: AgentSummary; onDeleted: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => api.adminAgentDelete(agent.id),
    onSuccess: onDeleted,
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '삭제 실패');
    },
  });

  return (
    <div className="m-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
      <div className="mb-2 text-sm font-bold text-destructive">위험 구역</div>
      <div className="mb-3 text-xs text-muted-foreground">
        에이전트 삭제 시 발급된 모든 API 키도 삭제됩니다. 외부 서비스가 즉시 401 응답을 받음. 대화
        이력 (chat_sessions / chat_messages) 은 그대로 남되 agent 참조는 NULL 처리.
      </div>
      {error && <div className="mb-2 text-sm text-destructive">{error}</div>}
      <Button
        type="button"
        variant="danger"
        size="sm"
        onClick={() => {
          if (confirm(`정말 "${agent.name}" 을(를) 삭제할까요?`)) {
            mutation.mutate();
          }
        }}
        disabled={mutation.isPending}
      >
        에이전트 삭제
      </Button>
    </div>
  );
}
